import { useCallback, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import crmEnrichmentTrackingService from "app/pages/sales-intelligence/modules/crm-enrichment/services/tracking/trackingService";

import EnrichmentLogsBreadcrumbs from "../../components/EnrichmentLogsBreadcrumbs/EnrichmentLogsBreadcrumbs";
import EnrichmentLogsTable from "../../components/EnrichmentLogsTable/EnrichmentLogsTable";
import { useCrmEnrichmentContext } from "../../contexts/CrmEnrichmentContext";
import useDownloadRunResultFile from "../../hooks/useDownloadRunResultFile";
import useEnrichmentJobQuery from "../../query/queries/useEnrichmentJobQuery";
import useEnrichmentRunHistoryQuery from "../../query/queries/useEnrichmentRunHistoryQuery";

import { StyledLogsPageContentFlex, StyledLogsPageWrapper } from "./styles";

const DEFAULT_PAGE_SIZE = 25;

const EnrichmentLogsContainer = () => {
    const { params } = useCurrentStateAndParams<{ jobId: string }>();
    const { jobId } = params;

    const { crmProvider } = useCrmEnrichmentContext();
    const { mutate: downloadRunResultFile } = useDownloadRunResultFile(crmProvider);

    const [page, setPage] = useState(1);

    const jobQuery = useEnrichmentJobQuery(jobId);
    const runHistoryQuery = useEnrichmentRunHistoryQuery(jobId, page, DEFAULT_PAGE_SIZE);

    const handlePageChange = useCallback((_currentPage: number, _pageSize: number, nextPage: number) => {
        crmEnrichmentTrackingService.trackLogsPagination(nextPage);

        setPage(nextPage);
    }, []);

    const handleDownloadCsv = useCallback(
        (runId: string) => {
            crmEnrichmentTrackingService.trackDownloadLogs("enrichment logs page");

            downloadRunResultFile({ jobId, runId });
        },
        [downloadRunResultFile, jobId],
    );

    const jobName = jobQuery.data?.name ?? "";

    return (
        <StyledLogsPageWrapper>
            <EnrichmentLogsBreadcrumbs integrationName={crmProvider} />
            <StyledLogsPageContentFlex
                flexDirection="column"
                gap="24px"
            >
                {jobName ? (
                    <Text
                        fontVariant="h4"
                        color={colorsPalettes.carbon["400"]}
                    >
                        {`${jobName} — ${translate("si.crm-enrichment.logs.title")}`}
                    </Text>
                ) : null}

                <EnrichmentLogsTable
                    integrationName={crmProvider}
                    data={runHistoryQuery.data}
                    isLoading={runHistoryQuery.isLoading}
                    page={page}
                    pageSize={DEFAULT_PAGE_SIZE}
                    jobType={jobQuery.data?.type}
                    onPageChange={handlePageChange}
                    onDownloadCsv={handleDownloadCsv}
                />
            </StyledLogsPageContentFlex>
        </StyledLogsPageWrapper>
    );
};

export default EnrichmentLogsContainer;
